import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Plus, Users, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useChats } from '@/hooks/useChats';
import { Chat } from '@/types/chat';
import { formatDistanceToNow } from 'date-fns';
import { ShareModal } from '@/components/ShareModal';

const Inbox: React.FC = () => {
  const { chats, loading, getTotalUnreadCount } = useChats();
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);

  const filteredChats = chats.filter((chat) => {
    if (!searchQuery) return true;
    
    const searchLower = searchQuery.toLowerCase();
    
    if (chat.is_group && chat.title) {
      return chat.title.toLowerCase().includes(searchLower);
    }
    
    // For DMs, search by the other user's name/username
    const otherMember = chat.members?.find(m => m.user_id !== chat.created_by);
    if (otherMember?.user) {
      return (
        otherMember.user.name?.toLowerCase().includes(searchLower) ||
        otherMember.user.username?.toLowerCase().includes(searchLower)
      );
    }
    
    return false;
  });

  const getChatTitle = (chat: Chat) => {
    if (chat.is_group) {
      return chat.title || 'Group Chat';
    }
    
    // For DM, show the other user's name
    const otherMember = chat.members?.find(m => m.user_id !== chat.created_by);
    return otherMember?.user?.name || otherMember?.user?.username || 'Unknown User';
  };

  const getChatAvatar = (chat: Chat) => {
    if (chat.is_group) {
      // For groups, show group icon or initials
      return null;
    }
    
    const otherMember = chat.members?.find(m => m.user_id !== chat.created_by);
    return otherMember?.user?.image;
  };

  const getChatAvatarFallback = (chat: Chat) => {
    if (chat.is_group) {
      return <Users className="h-4 w-4" />;
    }
    
    const otherMember = chat.members?.find(m => m.user_id !== chat.created_by);
    const name = otherMember?.user?.name || otherMember?.user?.username || 'U';
    return name.charAt(0).toUpperCase();
  };

  const getLastMessagePreview = (chat: Chat) => {
    if (!chat.last_message) return 'No messages yet';
    
    if (chat.last_message.type === 'share') {
      const kind = chat.last_message.share_payload?.kind;
      return `Shared a ${kind}`;
    }
    
    return chat.last_message.text || 'Message';
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading chats...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Inbox
              {getTotalUnreadCount() > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {getTotalUnreadCount()}
                </Badge>
              )}
            </CardTitle>
            <Button onClick={() => setShowNewChatModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Chat
            </Button>
          </div>
          
          <div className="flex items-center gap-2 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {filteredChats.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                {searchQuery ? 'No chats found' : 'No conversations yet'}
              </p>
              {!searchQuery && (
                <Button onClick={() => setShowNewChatModal(true)}>
                  Start Your First Chat
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredChats.map((chat) => (
                <Link
                  key={chat.id}
                  to={`/chat/${chat.id}`}
                  className="block"
                >
                  <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={getChatAvatar(chat) || undefined} />
                      <AvatarFallback>
                        {getChatAvatarFallback(chat)}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold truncate">
                          {getChatTitle(chat)}
                        </h3>
                        <div className="flex items-center gap-2">
                          {chat.last_message?.created_at && (
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(chat.last_message.created_at), { addSuffix: true })}
                            </span>
                          )}
                          {chat.unread_count && chat.unread_count > 0 && (
                            <Badge variant="destructive" className="text-xs">
                              {chat.unread_count}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-sm text-muted-foreground truncate">
                        {getLastMessagePreview(chat)}
                      </p>
                      
                      {chat.is_group && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {chat.members?.length || 0} members
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ShareModal
        isOpen={showNewChatModal}
        onClose={() => setShowNewChatModal(false)}
        mode="new-chat"
      />
    </div>
  );
};

export default Inbox;